import { ThemeOptions, createTheme } from "@mui/material/styles";
import type { Shadows } from "@mui/material/styles/shadows";

declare module "@mui/material/styles" {
  // @ts-ignore
  interface PaletteColorOptions {
    primary?: string;
    secondary?: string;
    inverse?: string;
    disabled?: string;
  }

  interface Palette {
    brand: PaletteColorOptions;
    brandSuccess: PaletteColorOptions;
    brandDanger: PaletteColorOptions;
    brandWarning: PaletteColorOptions;
    brandInfo: PaletteColorOptions;
    brandBackground: PaletteColorOptions;
    brandText: PaletteColorOptions;
    brandBorder: PaletteColorOptions;
  }

  interface PaletteOptions {
    brand?: PaletteColorOptions;
    brandSuccess?: PaletteColorOptions;
    brandDanger?: PaletteColorOptions;
    brandWarning?: PaletteColorOptions;
    brandInfo?: PaletteColorOptions;
    brandBackground?: PaletteColorOptions;
    brandText?: PaletteColorOptions;
    brandBorder?: PaletteColorOptions;
  }

  interface TypographyVariants {
    headingXL: React.CSSProperties;
    headingLG: React.CSSProperties;
    headingMD: React.CSSProperties;
    headingSM: React.CSSProperties;
    headingXS: React.CSSProperties;
    labelMD: React.CSSProperties;
    labelSM: React.CSSProperties;
    labelXS: React.CSSProperties;
    textMD: React.CSSProperties;
    textSM: React.CSSProperties;
    textXS: React.CSSProperties;
    textXXS: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    headingXL?: React.CSSProperties;
    headingLG?: React.CSSProperties;
    headingMD?: React.CSSProperties;
    headingSM?: React.CSSProperties;
    headingXS?: React.CSSProperties;
    labelMD?: React.CSSProperties;
    labelSM?: React.CSSProperties;
    labelXS?: React.CSSProperties;
    textMD?: React.CSSProperties;
    textSM?: React.CSSProperties;
    textXS?: React.CSSProperties;
    textXXS?: React.CSSProperties;
  }
}

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    headingXL: true;
    headingLG: true;
    headingMD: true;
    headingSM: true;
    headingXS: true;
    labelMD: true;
    labelSM: true;
    labelXS: true;
    textMD: true;
    textSM: true;
    textXS: true;
    textXXS: true;
  }
}

declare module "@mui/material/Button" {
  interface ButtonPropsColorOverrides {
    brand: true;
    brandDanger: true;
  }
}

const themeOptions: ThemeOptions = {
  shadows: Array(25).fill("none") as Shadows,
  palette: {
    // @ts-ignore
    primary: { main: "#264B9F" },
    brand: { primary: "#264B9F", secondary: "#264B9F" },
    brandSuccess: { primary: "#2CB666", secondary: "#E5F9EC" },
    brandDanger: { primary: "#EC1F3D", secondary: "#FEE9E8" },
    brandWarning: { primary: "#EACA00" },
    brandInfo: { primary: "#3280E8", secondary: "#E5F1FF" },
    brandBackground: {
      primary: "#FAF9F6",
      secondary: "#264B9F",
    },
    brandText: {
      primary: "#111726",
      secondary: "#A2A8B6",
      disabled: "#B7C1CF",
      inverse: "#ffffff",
    },
    brandBorder: {
      primary: "#F0F1F5",
    },
    background: {
      default: "#FAF9F6",
    },
  },
  typography: {
    fontFamily: '"Maxima Nouva", "Lato", "Arial", sans-serif',
    headingXL: { fontSize: 40, fontWeight: 600 },
    headingLG: { fontSize: 32, fontWeight: 600 },
    headingMD: { fontSize: 28, fontWeight: 600 },
    headingSM: { fontSize: 24, fontWeight: 600 },
    headingXS: { fontSize: 20, fontWeight: 600 },
    labelMD: { fontSize: 16, fontWeight: 500 },
    labelSM: { fontSize: 14, fontWeight: 500 },
    labelXS: { fontSize: 12, fontWeight: 500 },
    textMD: { fontSize: 16, fontWeight: 400 },
    textSM: { fontSize: 14, fontWeight: 400 },
    textXS: { fontSize: 12, fontWeight: 400 },
    textXXS: { fontSize: 10, fontWeight: 400 },
    allVariants: {
      color: "#1C1C1C",
      display: "flex",
    },
  },
  components: {
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          headingXL: "h1",
          headingLG: "h2",
          headingMD: "h3",
          headingSM: "h4",
          headingXS: "h5",
          labelMD: "p",
          labelSM: "p",
          labelXS: "p",
          textMD: "p",
          textSM: "p",
          textXS: "p",
          textXXS: "p",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: "2px 5px 8px rgba(107, 87, 29, 0.05)",
        },
      },
    },
    MuiButton: {
      defaultProps: { size: "small" },
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor:
            (theme.palette as any).brand?.primary ?? theme.palette.primary.main,
          textTransform: "none",
        }),
      },
    },
    MuiInputBase: {
      defaultProps: { margin: "dense" },
      styleOverrides: {
        input: {
          "&::placeholder": {
            color: "#E2E8E3",
            opacity: 1,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          backgroundColor: "#FFF",
        },
        notchedOutline: {
          borderColor: "#E2E8E3",
        },
      },
    },
    MuiInputLabel: { defaultProps: { margin: "dense" } },
    MuiTextField: {
      defaultProps: { margin: "dense", size: "small" },
      styleOverrides: { root: { margin: 0 } },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: ({ theme }) => ({
          marginTop: 2,
          borderRadius: 6,
          border: `1px solid ${
            (theme.palette as any).brandBorder?.primary ?? "#F0F1F5"
          }`,
          width: "100%",
          minWidth: 0,
        }),
        listbox: {
          paddingTop: 8,
          paddingBottom: 8,
          width: "100%",
        },
        option: {
          width: "100%",
          boxSizing: "border-box",
        },
        noOptions: ({ theme }) => ({
          paddingTop: 12,
          paddingBottom: 12,
          fontSize: 16,
          color: (theme.palette as any).brandText?.secondary ?? "#818181",
        }),
        loading: ({ theme }) => ({
          paddingTop: 8,
          paddingBottom: 8,
          fontSize: 16,
          color: (theme.palette as any).brandText?.secondary ?? "#818181",
        }),
      },
    },
    MuiUseMediaQuery: { defaultProps: { noSsr: true } },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          paddingLeft: 24,
          paddingRight: 24,
        },
      },
    },
  },
};

const theme = createTheme(themeOptions);

export default theme;
